import { motion, AnimatePresence, useMotionTemplate, useMotionValue } from 'framer-motion'
import { useEffect, useState, type MouseEvent } from 'react'
import { useIsMobile } from '../lib/useIsMobile'
import { useT } from '../i18n/LocaleContext'

type Carrier = {
  id: string
  name: string
  file: string
  zones: string[]
  lead: string
}

// Données brutes (noms/fichiers/zones inchangés) ; le libellé de zone « Monde »
// et la description (lead) sont localisés à l'exécution via useT.
const CARRIER_DATA: { id: string; name: string; file: string; zones: string[] }[] = [
  { id: 'bpost',         name: 'bpost',          file: 'Bpost.svg',          zones: ['BE', 'LU', 'NL'] },
  { id: 'gls',           name: 'GLS',            file: 'Gls.svg',            zones: ['EU'] },
  { id: 'dhl',           name: 'DHL',            file: 'DHL.svg',            zones: ['Monde'] },
  { id: 'dpd',           name: 'DPD',            file: 'DPD.svg',            zones: ['EU'] },
  { id: 'postnl',        name: 'PostNL',         file: 'PostNl.svg',         zones: ['NL', 'BE'] },
  { id: 'ups',           name: 'UPS',            file: 'ups.svg',            zones: ['Monde'] },
  { id: 'fedex',         name: 'FedEx',          file: 'Fedex.svg',          zones: ['Monde'] },
  { id: 'mondialrelay',  name: 'Mondial Relay',  file: 'mondial relay.svg',  zones: ['EU'] },
  { id: 'colisprive',    name: 'Colis Privé',    file: 'Colis Prive.svg',    zones: ['FR', 'BE', 'LU'] },
  { id: 'deutschepost',  name: 'Deutsche Post',  file: 'Deutsche Post.svg',  zones: ['DE'] },
  { id: 'dsv',           name: 'DSV',            file: 'DSV.svg',            zones: ['Monde'] },
]

// Clé de description par id de transporteur.
const LEAD_KEYS: Record<string, string> = {
  bpost: 'carriers.leadBpost',
  gls: 'carriers.leadGls',
  dhl: 'carriers.leadDhl',
  dpd: 'carriers.leadDpd',
  postnl: 'carriers.leadPostnl',
  ups: 'carriers.leadUps',
  fedex: 'carriers.leadFedex',
  mondialrelay: 'carriers.leadMondialrelay',
  colisprive: 'carriers.leadColisprive',
  deutschepost: 'carriers.leadDeutschepost',
  dsv: 'carriers.leadDsv',
}

export default function Carriers() {
  const [activeIdx, setActiveIdx] = useState(0)
  const [paused, setPaused] = useState(false)
  const { t } = useT()

  // Construit la liste localisée : la zone « Monde » devient t('carriers.zoneWorld'),
  // la description vient de la clé de namespace correspondante.
  const CARRIERS: Carrier[] = CARRIER_DATA.map((c) => ({
    ...c,
    zones: c.zones.map((z) => (z === 'Monde' ? t('carriers.zoneWorld') : z)),
    lead: t(LEAD_KEYS[c.id]),
  }))

  useEffect(() => {
    if (paused) return
    const timer = setInterval(() => {
      setActiveIdx((i) => (i + 1) % CARRIER_DATA.length)
    }, 2800)
    return () => clearInterval(timer)
  }, [paused])

  const active = CARRIERS[activeIdx]

  return (
    <section
      id="carriers"
      className="relative isolate py-20 sm:py-24 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Background layer — all decorative elements behind content (-z-10) */}
      <div aria-hidden className="absolute inset-0 -z-10 pointer-events-none">
        {/* Atterrissage doux après le hero nuit : léger voile bleu qui s'éteint */}
        <div
          className="absolute inset-x-0 top-0 h-44"
          style={{
            background: 'linear-gradient(to bottom, rgba(59,93,255,0.05), transparent)',
          }}
        />
        {/* Bottom fade into Services */}
        <div
          className="absolute inset-x-0 -bottom-1 h-32"
          style={{
            background: 'linear-gradient(to bottom, transparent, rgba(14,14,20,0.03))',
          }}
        />
      </div>

      <div className="relative max-w-6xl mx-auto px-6">
        {/* Section header */}
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
            </span>
            <span className="font-medium">{t('carriers.badgeLabel')}</span>
            <span className="text-accent-400">·</span>
            <span className="text-accent-700">{t('carriers.badgeCount')}</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 font-semibold tracking-[-0.02em] text-ink-950 text-[clamp(2rem,4.5vw,3.5rem)] leading-[1.05] max-w-3xl"
          >
            {t('carriers.headlinePart1')}{' '}
            <span className="font-semibold bg-gradient-to-r from-accent-600 via-accent-500 to-accent-700 bg-clip-text text-transparent pb-[0.1em] inline-block">
              {t('carriers.headlinePart2')}
            </span>
            ,<br />
            {t('carriers.headlinePart3')}
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="mt-5 max-w-2xl text-ink-600 leading-relaxed"
          >
            {t('carriers.subhead')}
          </motion.p>
        </div>

        {/* Focus card + picker */}
        <div className="mt-14 grid md:grid-cols-[1.1fr_1fr] gap-8 items-stretch">
          <FocusCard carrier={active} />
          <CarrierGrid
            carriers={CARRIERS}
            active={activeIdx}
            onPick={(i) => {
              setActiveIdx(i)
              setPaused(true)
            }}
          />
        </div>
      </div>
    </section>
  )
}

function FocusCard({ carrier }: { carrier: Carrier }) {
  const isMobile = useIsMobile()
  const { t } = useT()
  const mouseX = useMotionValue(-500)
  const mouseY = useMotionValue(-500)

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    if (isMobile) return
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    if (isMobile) return
    mouseX.set(-500)
    mouseY.set(-500)
  }

  const spotlight = useMotionTemplate`radial-gradient(440px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.10), transparent 60%)`

  return (
    /* Double-Bezel : plateau gris + plaque blanche, même matière que les
       cartes Services/Integrations — l'aplat gris terne devient un objet. */
    <motion.div
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative h-full rounded-[1.75rem] bg-ink-950/[0.04] p-1.5 ring-1 ring-ink-950/[0.06]"
    >
    <div className="relative h-full overflow-hidden rounded-[calc(1.75rem-0.375rem)] border border-ink-950/[0.05] bg-white p-7 sm:p-9 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
      {/* cursor spotlight — desktop only */}
      {!isMobile && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: spotlight }}
        />
      )}

      {/* pulsing radial behind logo — static on mobile */}
      {isMobile ? (
        <div
          aria-hidden
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none opacity-70"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.22), rgba(59,93,255,0) 70%)',
          }}
        />
      ) : (
        <motion.div
          aria-hidden
          animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.08, 1] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(59,93,255,0.22), rgba(59,93,255,0) 70%)',
            filter: 'blur(30px)',
          }}
        />
      )}

      <div className="relative flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-accent-700 font-semibold">
        <span className="w-6 h-px bg-gradient-to-r from-accent-500 to-accent-300" />
        {t('carriers.featured')}
      </div>

      <div className="relative mt-6 min-h-[240px] flex flex-col justify-between">
        <AnimatePresence mode="wait">
          <motion.div
            key={carrier.id}
            initial={{ opacity: 0, y: 14, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            exit={{ opacity: 0, y: -14, filter: 'blur(8px)' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col gap-5"
          >
            <div className="relative h-24 flex items-center">
              <img
                src={`/carriers/${carrier.file}`}
                alt={carrier.name}
                width="160"
                height="80"
                className="max-h-20 w-auto object-contain"
                draggable={false}
              />
            </div>

            <div>
              <p className="text-2xl font-semibold text-ink-950 tracking-tight">
                {carrier.name}
              </p>
              <p className="mt-1 text-ink-600 leading-relaxed">{carrier.lead}</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {carrier.zones.map((z) => (
                <span
                  key={z}
                  className="inline-flex items-center rounded-full border border-accent-200 bg-accent-50 text-accent-800 text-[11px] font-medium px-2.5 py-1"
                >
                  {z}
                </span>
              ))}
              <span className="inline-flex items-center gap-1 text-[11px] text-ink-500 ml-1">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b5dff" strokeWidth="2.4">
                  <path d="M5 12l5 5L20 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                {t('carriers.negotiatedRates')}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Progress bar — cycles with the auto-rotate */}
        <motion.div
          key={carrier.id + '-bar'}
          className="relative mt-6 h-[2px] bg-ink-950/[0.06] rounded-full overflow-hidden"
        >
          <motion.div
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: 2.8, ease: 'linear' }}
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-accent-500 to-accent-700"
          />
        </motion.div>
      </div>
    </div>
    </motion.div>
  )
}

function CarrierGrid({
  carriers,
  active,
  onPick,
}: {
  carriers: Carrier[]
  active: number
  onPick: (i: number) => void
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      // md+ : les tuiles remplissent exactement la hauteur de la carte « En vedette »
      // (rangées explicites 1fr). 3 colonnes en tablette pour garder des tuiles
      // proportionnées, 4 colonnes × 3 rangées à partir de lg.
      className="relative grid grid-cols-4 gap-3 md:h-full md:grid-cols-3 md:grid-rows-4 lg:grid-cols-4 lg:grid-rows-3"
    >
      {carriers.map((c, i) => (
        <CarrierTile
          key={c.id}
          carrier={c}
          active={i === active}
          onClick={() => onPick(i)}
          delay={i * 0.035}
        />
      ))}
      <MoreTile delay={carriers.length * 0.035} />
    </motion.div>
  )
}

function CarrierTile({
  carrier,
  active,
  onClick,
  delay,
}: {
  carrier: Carrier
  active: boolean
  onClick: () => void
  delay: number
}) {
  const isMobile = useIsMobile()
  const mouseX = useMotionValue(-200)
  const mouseY = useMotionValue(-200)
  const [hover, setHover] = useState(false)

  const onMove = (e: MouseEvent<HTMLButtonElement>) => {
    if (isMobile) return
    const r = e.currentTarget.getBoundingClientRect()
    mouseX.set(e.clientX - r.left)
    mouseY.set(e.clientY - r.top)
  }
  const onLeave = () => {
    if (isMobile) return
    setHover(false)
    mouseX.set(-200)
    mouseY.set(-200)
  }

  const glow = useMotionTemplate`radial-gradient(160px circle at ${mouseX}px ${mouseY}px, rgba(59,93,255,0.7), transparent 70%)`

  return (
    <motion.button
      onClick={onClick}
      onMouseEnter={() => !isMobile && setHover(true)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={isMobile ? undefined : { y: -3 }}
      whileTap={{ scale: 0.96 }}
      className={`relative group aspect-[5/3] md:aspect-auto md:h-full rounded-xl overflow-hidden border transition-[border-color,box-shadow] duration-300 ${
        active
          ? 'border-accent-400 bg-white ring-1 ring-accent-200 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_18px_44px_-18px_rgba(36,47,214,0.35)]'
          : 'border-ink-950/[0.08] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] hover:border-accent-200'
      }`}
      aria-label={carrier.name}
    >
      {/* cursor-following blue border glow on hover — desktop only */}
      {!isMobile && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -inset-px rounded-xl opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ background: glow }}
        />
      )}

      {/* active ring pulse — desktop only (mobile gets a static highlight via border) */}
      {active && !isMobile && (
        <motion.div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            animate={{ opacity: [0.3, 0.7, 0.3] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at center, rgba(59,93,255,0.14), transparent 70%)',
            }}
          />
        </motion.div>
      )}

      {/* logo */}
      <div className="absolute inset-0 z-10 flex items-center justify-center p-3">
        <motion.img
          src={`/carriers/${carrier.file}`}
          alt={carrier.name}
          draggable={false}
          animate={{
            filter: active || hover ? 'grayscale(0) opacity(1)' : 'grayscale(1) opacity(0.55)',
            scale: active ? 1.05 : 1,
          }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="max-h-10 sm:max-h-11 md:max-h-14 w-auto object-contain"
        />
      </div>
    </motion.button>
  )
}

function MoreTile({ delay }: { delay: number }) {
  const [hover, setHover] = useState(false)
  const { t } = useT()
  return (
    <motion.div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, scale: 0.92 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3 }}
      className="relative group aspect-[5/3] md:aspect-auto md:h-full rounded-xl overflow-hidden border border-dashed border-accent-300 bg-gradient-to-br from-accent-50 via-white to-accent-50 transition-colors duration-300 flex items-center justify-center text-center p-3"
    >
      {/* Floating tiny glyphs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
        {[
          { x: '12%', y: '22%', d: 1.2 },
          { x: '82%', y: '18%', d: 1.8 },
          { x: '18%', y: '78%', d: 2.4 },
          { x: '78%', y: '74%', d: 3.0 },
        ].map((p, i) => (
          <motion.span
            key={i}
            className="absolute w-1.5 h-1.5 rounded-full bg-accent-400/70"
            style={{ left: p.x, top: p.y }}
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2.6, delay: p.d, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}
      </div>

      <div className="relative flex flex-col items-center gap-1">
        <motion.div
          animate={{ rotate: hover ? 90 : 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="w-7 h-7 rounded-full bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center shadow-[0_10px_24px_-10px_rgba(59,93,255,0.7)]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.6">
            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
        <p className="text-[11px] font-semibold text-accent-800 tracking-tight leading-tight">
          {t('carriers.moreCarriersLine1')}
          <br />
          {t('carriers.moreCarriersLine2')}
        </p>
      </div>
    </motion.div>
  )
}
